import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import {
  AbsenceReportTarget,
  TimeReportsHttpService,
  WorkTimeReportTarget
} from '@app/shared/services/http/time-reports-http.service';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { TranslateService } from '@ngx-translate/core';
import { forEach } from 'lodash';
import { catchError, concatMap, defer, forkJoin, from, map, Observable, of, tap, throwError, toArray } from 'rxjs';
import { TimeReport, TimeReportStatus } from '../types/models/reports/time-report';
import { TimeReportSeries } from '../types/models/reports/time-report-series';
import { WorkTimeReport } from '../types/models/reports/work-time-report';
import {
  RemoteReportOccurrence,
  remoteReportOccurrences,
  SeriesOccurrenceError
} from '../utils/remote-report-occurrences';
import { ApiErrorService } from './api-error.service';
import { CompaniesDataService } from './companies-data.service';
import { CompanyIndustriesDataService } from './company-industries-data.service';
import { DataService } from './data-service';
import { ContextDataDTO } from './dtos/context-data.dto';
import { AbsenceReportDTO } from './dtos/reports/absence-report.dto';
import { ModifyAbsenceReportModel, toModifyAbsenceReportBody } from './dtos/reports/modify-absence-report.model';
import { TimeReportDTO } from './dtos/reports/time-report.dto';
import { TimeReportFactory } from './factories/reports/time-report.factory';
import { GroupsDataService } from './groups-data.service';
import { LocationsDataService } from './locations-data.service';
import { ProjectIndustriesDataService } from './project-industries-data.service';
import { ProjectParticipantsDataService } from './project-participants-data.service';
import { ProjectsDataService } from './projects-data.service';
import { ToastService } from './shared/toast.service';
import { TaskParticipantsDataService } from './task-participants-data.service';
import { TaskTypesDataService } from './task-types-data.service';
import { TasksDataService } from './tasks-data.service';
import { TimeReportSeriesFeatureService } from './time-report-series-feature.service';
import { UsersDataService } from './users-data.service';

type TimeReportApprovalResult = { isApproved: true } | { isApproved: false; error: unknown };

/** Endpoint family and owner of a series write; part of the key that remembers its completed days. */
type SeriesWriteTarget = ['work-time', WorkTimeReportTarget] | ['absence', AbsenceReportTarget];

/**
 * TimeReportsDataService - Loads mixed time reports and performs WorkTime / Absence writes.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Reports views and time-report dialogs call this service for list, create, update, and delete.
 * WorkTime create/update stay on `tasks/{taskId}/work-time-reports` and `work-time-reports/{id}`.
 * Absence create/update use `companies/{companyId}/absence-reports` and `absence-reports/{id}`.
 * Multi-day series (test API only, see {@link TimeReportSeriesFeatureService}) are written as one
 * regular create POST per selected day, sequentially, on the same endpoints.
 */
@Injectable({ providedIn: 'root' })
export class TimeReportsDataService extends DataService<TimeReportDTO, TimeReport> {
  private readonly timeReports: DataStore<TimeReport> = new DataStore<TimeReport>();
  private readonly userTimeReports: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly taskTimeReports: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  private readonly timeReportsHttpService: TimeReportsHttpService = inject(TimeReportsHttpService);
  private readonly timeReportFactory: TimeReportFactory = inject(TimeReportFactory);
  private readonly companiesService: CompaniesDataService = inject(CompaniesDataService);
  private readonly groupsService: GroupsDataService = inject(GroupsDataService);
  private readonly industriesService: CompanyIndustriesDataService = inject(CompanyIndustriesDataService);
  private readonly locationsService: LocationsDataService = inject(LocationsDataService);
  private readonly projectsService: ProjectsDataService = inject(ProjectsDataService);
  private readonly projectIndustriesService: ProjectIndustriesDataService = inject(ProjectIndustriesDataService);
  private readonly projectParticipantsService: ProjectParticipantsDataService = inject(ProjectParticipantsDataService);
  private readonly taskTypesService: TaskTypesDataService = inject(TaskTypesDataService);
  private readonly tasksService: TasksDataService = inject(TasksDataService);
  private readonly usersService: UsersDataService = inject(UsersDataService);
  private readonly taskParticipantsService: TaskParticipantsDataService = inject(TaskParticipantsDataService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly apiErrorService: ApiErrorService = inject(ApiErrorService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly seriesFeature: TimeReportSeriesFeatureService = inject(TimeReportSeriesFeatureService);
  /** Days already persisted per series payload, so a retry of the same form only writes the rest. */
  private readonly completedSeriesDays = new Map<string, Map<string, TimeReportDTO[]>>();
  /** Series payloads whose last write had an unknown outcome; retrying could duplicate a day. */
  private readonly uncertainSeriesBatches = new Set<string>();

  public getTimeReports(dateFrom: Date, dateTo: Date): Signal<TimeReport[] | undefined> {
    return computed(
      () =>
        this.userTimeReports
          .get(this.getCollectionKey(dateFrom, dateTo))()
          ?.ids?.map(id => this.timeReports.get(id)())
          .filter(report => report !== undefined) || []
    );
  }

  public getById(timeReportId: string): Signal<TimeReport | undefined> {
    return computed(() => this.timeReports.get(timeReportId)());
  }

  public getTaskTimeReports(taskId: string, dateFrom: Date, dateTo: Date): Signal<TimeReport[] | undefined> {
    return computed(
      () =>
        this.taskTimeReports
          .get(this.getTaskCollectionKey(taskId, dateFrom, dateTo))()
          ?.ids?.map(id => this.timeReports.get(id)())
          .filter(report => report !== undefined) || []
    );
  }

  public loadTaskTimeReports(taskId: string, dateFrom: Date, dateTo: Date): void {
    const key = this.getTaskCollectionKey(taskId, dateFrom, dateTo);
    if (this.taskTimeReports.hasDataForId(key)) {
      return;
    }
    this.fetchTaskTimeReports(taskId, dateFrom, dateTo);
  }

  private getTaskCollectionKey(taskId: string, dateFrom: Date, dateTo: Date): string {
    return `${taskId}-${this.getCollectionKey(dateFrom, dateTo)}`;
  }

  public loadTimeReports(dateFrom: Date, dateTo: Date): void {
    // The range key lives in the userTimeReports collection store (not the entity store),
    // mirroring loadTaskTimeReports and every other data service. reloadTimeReports bypasses this.
    if (this.userTimeReports.hasDataForId(this.getCollectionKey(dateFrom, dateTo))) {
      return;
    }
    this.fetchTimeReports(dateFrom, dateTo);
  }

  /**
   * Always fetches mixed GET /time-reports for the range, replacing that collection's ids.
   * Does not use the {@link loadTimeReports} cache guard.
   */
  public reloadTimeReports(dateFrom: Date, dateTo: Date): void {
    this.fetchTimeReports(dateFrom, dateTo);
  }

  public upsertLocalData(dto: TimeReportDTO): Signal<TimeReport> {
    if (!dto) {
      return signal(undefined);
    }

    try {
      const model = this.timeReportFactory.produce(dto);
      return model ? this.timeReports.upsert(model) : signal(undefined);
    } catch (error: unknown) {
      console.error('Failed to map time report', error);
      return signal(undefined);
    }
  }

  protected getCollectionKey = (dateFrom: Date, dateTo: Date): string =>
    `${dateFrom.getFullYear()}-${dateFrom.getMonth()}-${dateFrom.getDate()}-${dateTo.getFullYear()}-${dateTo.getMonth()}-${dateTo.getDate()}`;

  protected fetchTimeReports(dateFrom: Date, dateTo: Date): void {
    const params = {
      dateFrom: this.toStartOfDayUTC(dateFrom),
      dateTo: this.toEndOfDayUTC(dateTo)
    };

    this.timeReportsHttpService.getTimeReports(params).subscribe({
      next: mixed => {
        this.uploadContextDataItems(mixed?.contextData);
        const reports = mixed?.data ?? [];
        reports.forEach(dto => this.upsertLocalData(dto));

        this.userTimeReports.upsert({
          id: this.getCollectionKey(dateFrom, dateTo),
          ids: reports.map(dto => dto.id)
        });
      }
    });
  }

  protected fetchTaskTimeReports(taskId: string, dateFrom: Date, dateTo: Date): void {
    const params = {
      dateFrom: this.toStartOfDayUTC(dateFrom),
      dateTo: this.toEndOfDayUTC(dateTo)
    };

    this.timeReportsHttpService.getTaskWorkTimeReports(taskId, params).subscribe({
      next: (data: TimeReportDTO[]) => {
        if (data?.length) {
          forEach(data, r => {
            this.upsertLocalData(r);
          });
        }

        this.taskTimeReports.upsert({
          id: this.getTaskCollectionKey(taskId, dateFrom, dateTo),
          ids: data?.map(_ => _.id) || []
        });
      }
    });
  }

  private uploadContextDataItems(contextData: ContextDataDTO): void {
    if (!contextData) {
      return;
    }

    contextData.companies?.forEach(dto => this.companiesService.upsertLocalData(dto));
    contextData.groups?.forEach(dto => this.groupsService.upsertLocalData(dto));
    contextData.industries?.forEach(dto => this.industriesService.upsertLocalData(dto));
    contextData.locations?.forEach(dto => this.locationsService.upsertLocalData(dto));
    contextData.projects?.forEach(dto => this.projectsService.upsertLocalData(dto));
    contextData.projectIndustries?.forEach(dto => this.projectIndustriesService.upsertLocalData(dto));
    contextData.projectParticipants?.forEach(dto => this.projectParticipantsService.upsertLocalData(dto));
    contextData.taskTypes?.forEach(dto => this.taskTypesService.upsertLocalData(dto));
    contextData.users?.forEach(dto => this.usersService.upsertLocalData(dto));
    contextData.tasks?.forEach(dto => this.tasksService.upsertLocalData(dto));

    contextData.projectTaskParticipants?.forEach(dto => {
      this.taskParticipantsService.upsertLocalData(dto);
      this.taskParticipantsService.upsertLocalDataTaskParticipants(dto);
    });
  }

  private toStartOfDayUTC(date: Date): string {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).toISOString();
  }

  private toEndOfDayUTC(date: Date): string {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).toISOString();
  }

  public addWorkTimeReport(
    taskId: string,
    workTimeReport: WorkTimeReport,
    files?: File[],
    descriptions?: string[],
    tags?: string[][]
  ): Observable<TimeReportDTO[]> {
    return this.createWorkTimeReport({ taskId }, workTimeReport, files, descriptions, tags);
  }

  public addWorkTimeReportForUser(
    taskId: string,
    userId: string,
    workTimeReport: WorkTimeReport,
    files?: File[],
    descriptions?: string[],
    tags?: string[][]
  ): Observable<TimeReportDTO[]> {
    return this.createWorkTimeReport({ taskId, userId }, workTimeReport, files, descriptions, tags);
  }

  private createWorkTimeReport(
    target: WorkTimeReportTarget,
    workTimeReport: WorkTimeReport,
    files?: File[],
    descriptions?: string[],
    tags?: string[][]
  ): Observable<TimeReportDTO[]> {
    const form = new FormData();

    files?.forEach((file, i) => {
      form.append(`files[${i}].File`, file, file.name);
      form.append(`files[${i}].OrderNo`, i.toString());
      if (descriptions?.[i]) form.append(`files[${i}].Description`, descriptions[i]);
      tags?.[i]?.forEach((tag, tagIndex) => {
        form.append(`files[${i}].Tags[${tagIndex}]`, tag);
      });
    });

    form.append(
      'attachments',
      JSON.stringify(
        (files ?? []).map((_, index) => ({
          orderNo: index,
          description: descriptions?.[index] ?? null,
          tags: tags?.[index] ?? []
        }))
      )
    );

    form.append('model', this.serializeWorkTimeReport(workTimeReport));

    return this.timeReportsHttpService.createWorkTimeReport(target, form).pipe(
      map((response: unknown) => this.normalizeReportWriteResponse(response)),
      tap(reports => {
        reports.forEach(timeReport => {
          this.upsertLocalData(timeReport);
          this.indexReportInLoadedCollections(timeReport, target.taskId);
        });
        this.tasksService.refreshTask(target.taskId);
        this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-created'));
      })
    );
  }

  public addAbsenceReport(companyId: string, request: ModifyAbsenceReportModel): Observable<AbsenceReportDTO[]> {
    const body = toModifyAbsenceReportBody(request);

    return this.timeReportsHttpService.createAbsenceReport({ companyId }, body).pipe(
      map((dtos: unknown) => this.normalizeReportWriteResponse(dtos) as AbsenceReportDTO[]),
      tap(dtos => {
        this.applyAbsenceWriteResponse(dtos);
        this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-created'));
      })
    );
  }

  /**
   * Creates an Absence report for a selected user (admin/permission-gated) via
   * `companies/{companyId}/users/{userId}/absence-reports`. Mirrors {@link addAbsenceReport};
   * the current-user route omits the `userId` segment.
   */
  public addAbsenceReportForUser(
    companyId: string,
    userId: string,
    request: ModifyAbsenceReportModel
  ): Observable<AbsenceReportDTO[]> {
    const body = toModifyAbsenceReportBody(request);

    return this.timeReportsHttpService.createAbsenceReport({ companyId, userId }, body).pipe(
      map((dtos: unknown) => this.normalizeReportWriteResponse(dtos) as AbsenceReportDTO[]),
      tap(dtos => {
        this.applyAbsenceWriteResponse(dtos);
        this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-created'));
      })
    );
  }

  /**
   * Creates one WorkTime report per selected series day (sequential POSTs on the regular create
   * endpoint). Attachments are sent with the first created day only.
   */
  public addWorkTimeReportSeries(
    taskId: string,
    workTimeReport: WorkTimeReport,
    series: TimeReportSeries,
    options: { userId?: string; files?: File[]; descriptions?: string[]; tags?: string[][] } = {}
  ): Observable<TimeReportDTO[]> {
    const target: WorkTimeReportTarget = { taskId, userId: options.userId };
    const model: Record<string, unknown> = JSON.parse(this.serializeWorkTimeReport(workTimeReport));

    return this.createSeriesOccurrences(
      ['work-time', target],
      model,
      series,
      (body, isFirstCreated) => {
        const form = new FormData();
        if (isFirstCreated) {
          options.files?.forEach((file, i) => {
            form.append(`files[${i}].File`, file, file.name);
            if (options.descriptions?.[i]) form.append(`files[${i}].Description`, options.descriptions[i]);
            if (options.tags?.[i]?.length) form.append(`files[${i}].Tags`, options.tags[i].join(';'));
          });
        }
        form.append('model', JSON.stringify(body));
        return this.timeReportsHttpService.createWorkTimeReport(target, form, true);
      },
      reports => {
        reports.forEach(report => {
          this.upsertLocalData(report);
          this.indexReportInLoadedCollections(report, taskId);
        });
        this.tasksService.refreshTask(taskId);
      }
    );
  }

  /**
   * Creates one Absence report per selected series day (sequential POSTs on the regular create
   * endpoint for the current or the selected user).
   */
  public addAbsenceReportSeries(
    companyId: string,
    request: ModifyAbsenceReportModel,
    series: TimeReportSeries,
    userId?: string
  ): Observable<AbsenceReportDTO[]> {
    const target: AbsenceReportTarget = { companyId, userId };
    const model = { ...toModifyAbsenceReportBody(request) } as Record<string, unknown>;

    return this.createSeriesOccurrences(
      ['absence', target],
      model,
      series,
      body => this.timeReportsHttpService.createAbsenceReport(target, body, true),
      reports => this.applyAbsenceWriteResponse(reports)
    ) as Observable<AbsenceReportDTO[]>;
  }

  /**
   * Sequential per-day writes. Every day is expanded (and DST-checked) before the first write.
   * Successful days are remembered per payload so an unchanged retry resumes with the rest.
   */
  private createSeriesOccurrences(
    writeTarget: SeriesWriteTarget,
    model: Record<string, unknown>,
    series: TimeReportSeries,
    post: (body: Record<string, unknown>, isFirstCreated: boolean) => Observable<unknown>,
    apply: (reports: TimeReportDTO[]) => void
  ): Observable<TimeReportDTO[]> {
    return defer(() => {
      if (!this.seriesFeature.available()) {
        this.toastService.error(this.translateService.instant('time-reports.toasts.remote-series-bridge-blocked'));
        return throwError(() => new Error('SERIES_UNAVAILABLE'));
      }

      let occurrences: RemoteReportOccurrence[];
      try {
        occurrences = remoteReportOccurrences(series);
      } catch (error: unknown) {
        this.toastSeriesExpansionError(error);
        return throwError(() => error);
      }

      const key = this.seriesBatchKey(writeTarget, model, series);
      if (this.uncertainSeriesBatches.has(key)) {
        this.toastService.error(this.translateService.instant('time-reports.toasts.remote-batch-uncertain'));
        return throwError(() => new Error('SERIES_OUTCOME_UNCERTAIN'));
      }

      const completed = this.completedSeriesDays.get(key) ?? new Map<string, TimeReportDTO[]>();
      this.completedSeriesDays.set(key, completed);

      return from(occurrences).pipe(
        concatMap(occurrence => {
          const existing = completed.get(occurrence.localDate);
          if (existing) {
            return of(existing);
          }

          const body: Record<string, unknown> = { ...model, ...occurrence };
          delete body['localDate'];
          // Legacy input modes are exclusive: Range must not also send a positive Duration.
          if (occurrence.dateFrom && occurrence.dateTo) {
            delete body['duration'];
          } else {
            delete body['dateFrom'];
            delete body['dateTo'];
          }

          return post(body, completed.size === 0).pipe(
            map(response => {
              const reports = this.normalizeReportWriteResponse(response);
              if (!reports.length || reports.some(report => !report.id)) {
                throw new Error('Missing persisted report identifiers');
              }
              completed.set(occurrence.localDate, reports);
              apply(reports);
              return reports;
            }),
            catchError((error: unknown) => {
              const uncertain =
                !(error instanceof HttpErrorResponse) ||
                error.status === 0 ||
                error.status === 408 ||
                error.status >= 500;
              if (uncertain) {
                this.uncertainSeriesBatches.add(key);
              }
              this.toastService.error(
                this.translateService.instant(
                  uncertain ? 'time-reports.toasts.remote-batch-uncertain' : 'time-reports.toasts.remote-batch-failed',
                  { date: occurrence.localDate, count: completed.size }
                )
              );
              return throwError(() => error);
            })
          );
        }),
        toArray(),
        map(reports => reports.flat()),
        tap(() => {
          this.completedSeriesDays.delete(key);
          this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-created'));
        })
      );
    });
  }

  private toastSeriesExpansionError(error: unknown): void {
    if (error instanceof SeriesOccurrenceError && error.kind === 'dstLocalTime') {
      this.toastService.error(
        this.translateService.instant('time-reports.toasts.series-dst-time', {
          date: error.localDate,
          time: error.localTime
        })
      );
      return;
    }

    this.toastService.error(this.translateService.instant('time-reports.toasts.form-invalid'));
  }

  /** Stable key: form reconstruction can change property order without changing the payload. */
  private seriesBatchKey(
    writeTarget: SeriesWriteTarget,
    model: Record<string, unknown>,
    series: TimeReportSeries
  ): string {
    return JSON.stringify([writeTarget, model, series], (_property, value: unknown) =>
      value !== null && typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(Object.entries(value).sort(([left], [right]) => left.localeCompare(right)))
        : value
    );
  }

  /**
   * Adds a freshly created report to every already loaded collection whose date range covers
   * the reported day, so open lists pick it up without re-fetching.
   *
   * The report's own `dateFrom`/`dateTo` cannot be used to derive a single collection key:
   * manual reports carry no time range at all, and lists are keyed by the range they display
   * (day, week, month, ...) rather than by the range of an individual report.
   */
  private indexReportInLoadedCollections(dto: TimeReportDTO, taskId?: string): void {
    const reportDate = this.getReportDate(dto);
    if (!reportDate) {
      return;
    }

    this.addIdToCoveringCollections(this.userTimeReports, dto.id, reportDate);
    if (taskId) {
      this.addIdToCoveringCollections(this.taskTimeReports, dto.id, reportDate, `${taskId}-`);
    }
  }

  private addIdToCoveringCollections(
    store: DataStore<IdsCollection>,
    reportId: string,
    reportDate: Date,
    keyPrefix?: string
  ): void {
    for (const key of Array.from(store.store.keys())) {
      if (keyPrefix && !key.startsWith(keyPrefix)) {
        continue;
      }

      // Skip keys that were only reserved: filling them would mark the range as loaded.
      const collection = store.get(key)();
      if (!collection) {
        continue;
      }

      const range = this.parseCollectionKey(key);
      if (!range || reportDate < range.start || reportDate > range.end) {
        continue;
      }

      const ids = collection.ids ?? [];
      if (ids.includes(reportId)) {
        continue;
      }

      store.upsert({ id: key, ids: [...ids, reportId] });
    }
  }

  private parseCollectionKey(key: string): { start: Date; end: Date } | undefined {
    const segments = key.split('-');
    if (segments.length < 6) {
      return undefined;
    }

    const [fromYear, fromMonth, fromDay, toYear, toMonth, toDay] = segments.slice(-6).map(Number);
    if ([fromYear, fromMonth, fromDay, toYear, toMonth, toDay].some(Number.isNaN)) {
      return undefined;
    }

    return {
      start: new Date(fromYear, fromMonth, fromDay, 0, 0, 0, 0),
      end: new Date(toYear, toMonth, toDay, 23, 59, 59, 999)
    };
  }

  private getReportDate(dto: TimeReportDTO): Date | undefined {
    const rawDate = dto.date ?? dto.dateFrom;
    if (!rawDate) {
      return undefined;
    }

    const date = new Date(rawDate);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  public updateWorkTimeReport(
    workTimeReport: WorkTimeReport,
    files?: File[],
    descriptions?: string[],
    tags?: string[][]
  ): Observable<TimeReportDTO | undefined> {
    const form = new FormData();

    files?.forEach((file, i) => {
      form.append(`files[${i}].File`, file, file.name);
      form.append(`files[${i}].OrderNo`, i.toString());
      if (descriptions?.[i]) form.append(`files[${i}].Description`, descriptions[i]);
      tags?.[i]?.forEach((tag, tagIndex) => {
        form.append(`files[${i}].Tags[${tagIndex}]`, tag);
      });
    });

    form.append(
      'attachments',
      JSON.stringify(
        (files ?? []).map((_, index) => ({
          orderNo: index,
          description: descriptions?.[index] ?? null,
          tags: tags?.[index] ?? []
        }))
      )
    );

    form.append('model', this.serializeWorkTimeReport(workTimeReport));

    return this.timeReportsHttpService.updateWorkTimeReport(workTimeReport.id, form).pipe(
      map((response: unknown) => this.normalizeReportWriteResponse(response)),
      tap(reports => {
        const [dto] = reports;
        if (dto) {
          this.upsertLocalData(dto);
        }
        if (workTimeReport.taskId) {
          this.tasksService.refreshTask(workTimeReport.taskId);
        }
        this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-updated'));
      }),
      map((reports): TimeReportDTO | undefined => reports[0])
    );
  }

  public updateAbsenceReport(id: string, request: ModifyAbsenceReportModel): Observable<AbsenceReportDTO[]> {
    const body = toModifyAbsenceReportBody(request);
    // The backend deletes and recreates the whole registration on update, so every row of the
    // edited registration is replaced by the response and must leave the store and loaded lists.
    const registrationId = this.getById(id)()?.registration?.id;
    const previousIds = [...this.timeReports.store.entries()]
      .filter(
        ([reportId, report]) => reportId === id || (!!registrationId && report()?.registration?.id === registrationId)
      )
      .map(([reportId]) => reportId);

    return this.timeReportsHttpService.updateAbsenceReport(id, body).pipe(
      map((dtos: unknown) => this.normalizeReportWriteResponse(dtos) as AbsenceReportDTO[]),
      tap(reports => {
        if (reports.length) {
          this.removeReplacedReports(previousIds, reports);
        }
        this.applyAbsenceWriteResponse(reports);
        this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-updated'));
      })
    );
  }

  private removeReplacedReports(previousIds: readonly string[], replacements: readonly TimeReportDTO[]): void {
    for (const previousId of previousIds) {
      this.removeIdFromLoadedCollections(this.userTimeReports, previousId);
      this.removeIdFromLoadedCollections(this.taskTimeReports, previousId);
      if (!replacements.some(report => report.id === previousId)) {
        this.timeReports.delete(previousId);
      }
    }
  }

  private applyAbsenceWriteResponse(dtos: TimeReportDTO[] | null | undefined): void {
    for (const dto of dtos ?? []) {
      // Absence write endpoints may omit or default the discriminator, so force it before the
      // shared TimeReportFactory runs (which throws on an unrecognized `type`).
      const normalizedDto = { ...dto, type: 'AbsenceReport' as const } as AbsenceReportDTO;
      this.upsertLocalData(normalizedDto);
      this.indexReportInLoadedCollections(normalizedDto);
    }
  }

  private serializeWorkTimeReport(report: WorkTimeReport): string {
    return JSON.stringify({
      id: report.id,
      description: report.description,
      date: report.date,
      duration: report.duration,
      dateFrom: report.dateFrom,
      dateTo: report.dateTo,
      comments: report.comments,
      reportType: report.reportType,
      hoursType: report.hoursType,
      taskId: report.taskId,
      projectId: report.projectId,
      locationId: report.locationId,
      standardHoursDuration: report.standardHoursDuration,
      bankHoursDuration: report.bankHoursDuration,
      overtime50Duration: report.overtime50Duration,
      overtime100Duration: report.overtime100Duration,
      diet: report.diet,
      privateKm: report.privateKm,
      expenses: report.expenses,
      otherExpenses: report.otherExpenses
    });
  }

  private normalizeReportWriteResponse(response: unknown): TimeReportDTO[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response.filter((item): item is TimeReportDTO => !!item && typeof item === 'object');
    }

    if (typeof response !== 'object') {
      return [];
    }

    const envelope = response as { data?: unknown; contextData?: ContextDataDTO };
    if (Array.isArray(envelope.data)) {
      this.uploadContextDataItems(envelope.contextData);
      return envelope.data.filter((item): item is TimeReportDTO => !!item && typeof item === 'object');
    }

    return [response as TimeReportDTO];
  }

  public deleteTimeReport(reportId: string, dateFrom: Date, dateTo: Date, taskId?: string): Observable<void> {
    // dateFrom/dateTo stay on the public API for Reports and task-page callers.
    // Success cleanup scans every loaded collection, not only the passed range.
    void dateFrom;
    void dateTo;

    return this.timeReportsHttpService.deleteTimeReport(reportId).pipe(
      tap(() => {
        this.removeIdFromLoadedCollections(this.userTimeReports, reportId);
        this.timeReports.delete(reportId);

        if (taskId) {
          this.removeIdFromLoadedCollections(this.taskTimeReports, reportId, `${taskId}-`);
          this.tasksService.refreshTask(taskId);
        }

        this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-deleted'));
      })
    );
  }

  private removeIdFromLoadedCollections(store: DataStore<IdsCollection>, reportId: string, keyPrefix?: string): void {
    for (const key of Array.from(store.store.keys())) {
      if (keyPrefix && !key.startsWith(keyPrefix)) {
        continue;
      }

      const collection = store.get(key)();
      if (!collection) {
        continue;
      }

      const ids = collection.ids ?? [];
      if (!ids.includes(reportId)) {
        continue;
      }

      store.upsert({ id: key, ids: ids.filter(id => id !== reportId) });
    }
  }

  public approveTimeReports(reportIds: string[]): Observable<void[]> {
    const requests = reportIds.map(id =>
      this.timeReportsHttpService.approveTimeReport(id).pipe(
        tap(() => this.markReportApproved(id)),
        map((): TimeReportApprovalResult => ({ isApproved: true })),
        catchError((error: unknown) => of<TimeReportApprovalResult>({ isApproved: false, error }))
      )
    );

    // Wait for every submitted approval before reporting an error, so callers reload a settled batch.
    return forkJoin(requests).pipe(
      concatMap(results => this.toApprovalOutcome(results)),
      tap(() => this.toastService.success(this.translateService.instant('time-reports.toasts.successfully-approved')))
    );
  }

  private markReportApproved(reportId: string): void {
    const report = this.timeReports.get(reportId)();
    if (report && 'status' in report) {
      this.timeReports.upsert({ ...report, status: TimeReportStatus.APPROVED });
    }
  }

  private toApprovalOutcome(results: TimeReportApprovalResult[]): Observable<void[]> {
    const failures = results.filter(
      (result): result is Extract<TimeReportApprovalResult, { isApproved: false }> => !result.isApproved
    );
    if (failures.length === 0) {
      return of(results.map(() => undefined));
    }
    this.toastApprovalFailure(failures[0].error, failures.length < results.length);
    return throwError(() => failures[0].error);
  }

  private toastApprovalFailure(error: unknown, hasPartialSuccess: boolean): void {
    // A partial batch cannot be described by one API message; the caller reloads the real statuses.
    const message = hasPartialSuccess
      ? this.translateService.instant('time-reports.toasts.approval-batch-uncertain')
      : (this.apiErrorService.getMessage(error) ?? this.translateService.instant('toasts.operation-failed'));
    this.toastService.error(message);
  }
}
